import { Controller } from "@hotwired/stimulus"

// Picks only take effect on Apply: the tabs and options count them as they change,
// and Apply waits until the form differs from what is already applied.
export default class extends Controller {
  static targets = ["form", "tab", "tabClear", "panel", "apply", "clear", "panes", "results", "searchResults", "searchAll"]
  static values = { countUrl: String, resultsUrl: String, lessonsOne: String, lessonsOther: String }

  connect() {
    this.applied = this.serialize()
    this.refresh()
  }

  refresh(event) {
    this.panelTargets.forEach((panel, index) => {
      // A tab still loading keeps the count it came with.
      if (panel.querySelector("turbo-frame:not([complete])")) return
      const picked = panel.querySelectorAll("input:checked").length
      const count = "filtersSingle" in panel.dataset ? Math.min(picked, 1) : picked
      const clear = this.tabClearTargets[index]
      if (!clear) return

      clear.hidden = count === 0
      clear.querySelector("[data-tab-count]").firstElementChild.textContent = count
    })

    this.recount()
    if (!this.countUrlValue) this.toggle(this.applyTarget, this.serialize() === this.applied)
    this.toggle(this.clearTarget, !this.formTarget.querySelector("input:checked"))

    // Typing waits a beat before asking the server: lesson search finds by meaning, at a cost per query.
    clearTimeout(this.typing)
    this.typing = setTimeout(() => this.searchLessons(), 250)
    if (event?.target?.id === "filter-search") this.highlight(-1)
    else if (event?.type === "change" && this.query && event.target.closest("[data-search-result]")) this.showPicked(event.target)
  }

  // As the search changes: the count behind Show N lessons, the lesson results, and the last row's wording.
  searchLessons() {
    if (this.countUrlValue) this.search()
    if (this.hasSearchAllTarget) this.searchAllTarget.querySelector("span").textContent = this.searchAllTarget.dataset.label.replace("%{query}", this.query)
    if (!this.hasSearchResultsTarget) return

    if (!this.query) return this.searchResultsTarget.replaceChildren()
    const url = new URL(this.resultsUrlValue, location.origin)
    url.searchParams.set("name", this.query)
    this.searchResultsTarget.src = url.href
  }

  resultsLoaded() {
    this.highlight(this.highlighted ?? -1)
  }

  // Up and down move through the results; Enter opens the highlighted one. Nothing is
  // highlighted until an arrow is pressed, so Enter on its own searches every lesson.
  navigate(event) {
    if (!this.query) return
    const items = this.resultItems
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault()
      const step = event.key === "ArrowDown" ? 1 : -1
      this.highlight(Math.min(Math.max((this.highlighted ?? -1) + step, 0), items.length - 1))
    } else if (event.key === "Enter") {
      event.preventDefault()
      ;(items[this.highlighted] || this.searchAllTarget).click()
    }
  }

  highlight(index) {
    const items = this.resultItems
    items.forEach((item, i) => item.toggleAttribute("data-highlighted", i === index))
    this.highlighted = index
    items[index]?.scrollIntoView({ block: "nearest" })
  }

  searchAll() {
    this.formTarget.requestSubmit()
  }

  // A filter picked from the results: the search clears and its tab opens on the pick.
  showPicked(input) {
    const index = this.panelTargets.indexOf(input.closest("[data-filters-target='panel']"))
    const search = document.getElementById("filter-search")
    search.value = ""
    search.dispatchEvent(new Event("input", { bubbles: true }))
    this.tabTargets[index]?.click()
  }

  get query() {
    return (this.formTarget.elements.name?.value || "").trim()
  }

  get resultItems() {
    return [...this.element.querySelectorAll("[data-search-result]")].filter(item => item.offsetParent !== null)
  }

  // Each option counts the lessons it would show alongside the other picks: the other
  // filters, the curriculum answer and the typed search narrow it; its own filter's picks
  // don't, as ticking two in one filter means either. Show N lessons counts the same way.
  recount() {
    const options = [...this.formTarget.querySelectorAll("input[data-lessons]")]
    const lessonsOf = input => input.dataset.lessons.split(" ").filter(Boolean).map(Number)
    const picks = new Map()
    options.filter(option => option.checked).forEach(option => {
      picks.set(option.name, new Set([ ...(picks.get(option.name) || []), ...lessonsOf(option) ]))
    })
    const curriculum = this.curriculumLessons()
    const shows = (lesson, except) => (!this.named || this.named.has(lesson)) && (!curriculum || curriculum.has(lesson)) &&
      [...picks].every(([name, lessons]) => name === except || lessons.has(lesson))

    options.forEach(option => {
      const count = lessonsOf(option).filter(lesson => shows(lesson, option.name)).length
      const row = option.closest("label")
      row.querySelector("[data-trailing]").textContent = count
      row.toggleAttribute("data-empty", count === 0)
    })

    if (this.countUrlValue && this.named) {
      const total = [...this.named].filter(lesson => shows(lesson)).length
      this.applyTarget.querySelector("span").textContent = total === 1 ? this.lessonsOneValue : this.lessonsOtherValue.replace("%{count}", total)
    }
  }

  // The typed search matches lesson names, which only the server knows; it answers once per search.
  // Answers are kept while the window is open, so clearing the search counts again straight away.
  async search() {
    const name = this.formTarget.elements.name?.value || ""
    if (name === this.searched) return

    this.searching?.abort()
    this.answers ??= new Map()
    if (this.answers.has(name)) return this.answered(name, this.answers.get(name))

    this.searching = new AbortController()
    try {
      const url = new URL(this.countUrlValue, location.origin)
      url.searchParams.set("name", name)
      const response = await fetch(url, { signal: this.searching.signal, headers: { Accept: "application/json" } })
      const { lesson_ids: lessons } = await response.json()
      this.answers.set(name, lessons)
      this.answered(name, lessons)
    } catch (error) {
      if (error.name !== "AbortError") throw error
    }
  }

  answered(name, lessons) {
    this.searched = name
    this.named = new Set(lessons)
    this.recount()
  }

  curriculumLessons() {
    const deepest = [...this.formTarget.querySelectorAll("[data-curriculum-sections-target='step'] input:checked")].at(-1)
    return deepest && new Set(deepest.closest("label").dataset.lessons.split(" ").filter(Boolean).map(Number))
  }

  // Each filter opens at the top of its options; in a narrow window it takes over
  // the modal until All filters.
  drill() {
    if (this.hasResultsTarget) this.resultsTarget.scrollTop = 0
    if (this.hasPanesTarget) this.panesTarget.dataset.drilled = ""
  }

  back() {
    delete this.panesTarget.dataset.drilled
  }

  // One filter's picks, from the count beside its tab.
  clearFilter({ currentTarget }) {
    const panel = this.panelTargets[this.tabClearTargets.indexOf(currentTarget)]
    panel.querySelectorAll("input:checked").forEach(input => { input.checked = false })
    this.refresh()
    this.dispatch("cleared")
  }

  clear() {
    this.formTarget.querySelectorAll("input:checked").forEach(input => { input.checked = false })
    this.refresh()
    this.dispatch("cleared")
  }

  serialize() {
    return new URLSearchParams(new FormData(this.formTarget)).toString()
  }

  toggle(button, disabled) {
    button.disabled = disabled
    if (disabled) {
      button.setAttribute("aria-disabled", "true")
    } else {
      button.removeAttribute("aria-disabled")
    }
  }
}
